/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { Box } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import { height, style, width } from 'styled-system';

import { isSanityImage } from '@shared/utils';

import getAsset from '@/utils/getAsset';
import { SanityImage } from '../../../../../libs/shared/components';
import placeholder from './assets/placeholder.svg';

const OldStyledImage = styled.img`
  ${height}
  ${width}
      ${style({
    prop: 'fit',
    cssProperty: 'objectFit',
  })}
`;

const NextStyledImage = styled(NextImage)`
  ${height}
  ${width}
  ${style({ prop: 'fit', cssProperty: 'objectFit' })}
`;

const NewSanityImage = (image, alt) => (
  <SanityImage image={image} alt={alt} objectFit="cover" />
);

const StyledSanityImage = styled(NewSanityImage)`
  ${height}
  ${width}
  ${style({ prop: 'fit', cssProperty: 'objectFit' })}
`;

const Wrapper = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Placeholder = styled(OldStyledImage)`
  max-width: 50%;
`;

const renderImage = (imageProps) => {
  const { alt, src, setError, imageEnabled } = imageProps;
  if (imageEnabled) {
    return isSanityImage(src) ? (
      <StyledSanityImage
        {...imageProps}
        data-testid={'SANITY_CAROUSEL_IMAGE'}
        alt={alt}
        src={src}
        onError={() => setError(true)}
      />
    ) : (
      <NextStyledImage
        {...imageProps}
        data-testid="NEXT_CAROUSEL_IMAGE"
        src={src}
        alt={alt}
        layout="fill"
        objectFit="cover"
      />
    );
  }
  return (
    <OldStyledImage
      {...imageProps}
      data-testid={'OLD_CAROUSEL_IMAGE'}
      alt={alt}
      src={src}
      onError={() => setError(true)}
    />
  );
};

const Image = ({ alt = 'Image Coming Soon', src, ssr = false, ...props }) => {
  const [error, setError] = useState(!src);
  const [clientSide, setClientSide] = useState(ssr);
  const [isImageEnabled, setIsImageEnabled] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsImageEnabled(false);
    setClientSide(true);
    const urlHasFeature = router.asPath.includes('new-image');

    if (urlHasFeature) {
      setIsImageEnabled(true);
    }
  }, [router.asPath]);

  if (ssr) {
    return <OldStyledImage {...props} alt={alt} src={src} />;
  }

  if (error || !clientSide) {
    return (
      <Wrapper {...props} p="4" backgroundColor="greys.porcelain">
        <Placeholder width="75px" alt={alt} src={getAsset(placeholder)} />
      </Wrapper>
    );
  }

  const imageProps = {
    ...props,
    imageEnabled: isImageEnabled,
    alt,
    src,
    setError,
  };

  return renderImage(imageProps);
};

Image.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  ssr: PropTypes.bool,
};

export default Image;
